import { StatusValues, WineTypes, countries } from '@/common/constant';
import type { Status, Supplier } from '@/common/db-typebox';
import type {
  Country,
  WineType,
} from '@/common/generated/wine-product.typebox';
import { capitalize } from '@/common/utils';
import { FilterOutlined } from '@ant-design/icons';
import { Button, Card, Checkbox, Form, Select } from 'antd';
import { type FC, useEffect } from 'react';
import { type PriceRange, PriceRangeFilter } from './PriceRangeFilter';

export type WineFilter = {
  wineTypes: WineType[];
  regions: Country[];
  suppliers: string[];
  status: Status[];
  priceRange: PriceRange;
};

export const FilterCard: FC<{
  filter: WineFilter;
  suppliersData: Supplier[];
  onChange?: (filter: WineFilter) => void;
  onClear?: () => void;
}> = ({ filter, suppliersData, onChange, onClear }) => {
  const [form] = Form.useForm<WineFilter>();

  useEffect(() => {
    form.setFieldsValue(filter);
  }, [filter, form]);

  const handleValuesChange = (_: any, allValues: WineFilter) => {
    onChange?.(allValues);
  };

  const handleClear = () => {
    form.resetFields();
    onClear?.();
  };

  const wineTypeOptions = WineTypes.map((type) => ({
    label: capitalize(type),
    value: type,
  }));

  const regionOptions = countries.map((type) => ({
    label: capitalize(type),
    value: type,
  }));

  const availabilityOptions = StatusValues.map((status) => ({
    label: capitalize(status),
    value: status,
  }));

  return (
    <Card>
      <div className="flex flex-col gap-4 max-h-[65vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-1 m-0">
            <FilterOutlined style={{ fontSize: 20 }} />
            <span className="text-xl font-bold">Filters</span>
          </h3>
        </div>

        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleValuesChange}
          initialValues={filter}
        >
          <div className="space-y-4">
            <Form.Item name="wineTypes" label="Wine Type" className="mb-0">
              <Checkbox.Group options={wineTypeOptions} />
            </Form.Item>

            <Form.Item name="regions" label="Region" className="mb-0">
              <Select
                mode="multiple"
                placeholder="Select regions"
                options={regionOptions}
                className="w-full"
                maxTagCount="responsive"
                allowClear
              />
            </Form.Item>

            <Form.Item name="suppliers" label="Supplier" className="mb-0">
              <Checkbox.Group
                options={
                  suppliersData?.map((supplier) => ({
                    label: supplier.name,
                    value: supplier.id,
                  })) ?? []
                }
              />
            </Form.Item>

            <Form.Item name="priceRange" label="Price Range" className="mb-0">
              <PriceRangeFilter />
            </Form.Item>

            <Form.Item name="status" label="Availability" className="mb-0">
              <Checkbox.Group options={availabilityOptions} />
            </Form.Item>

            <Form.Item className="mb-0">
              <Button onClick={handleClear} className="w-full">
                Clear Filters
              </Button>
            </Form.Item>
          </div>
        </Form>
      </div>
    </Card>
  );
};
